import { withDeviceContext } from './deviceContext';

export type AnalyticsContextUser = {
  id?: number | null;
  username?: string | null;
  is_staff?: boolean;
  organization?: {
    id?: string | null;
    name?: string | null;
  } | null;
  cohorts?: Array<{
    id?: string | null;
    name?: string | null;
    role?: string | null;
    is_current?: boolean;
  }> | null;
};

type BuildAnalyticsEventPayloadOptions = {
  pathname?: string;
};

const getCurrentCohort = (user?: AnalyticsContextUser | null) => {
  if (!user?.cohorts?.length) {
    return null;
  }
  return user.cohorts.find((cohort) => cohort.is_current) ?? user.cohorts[0] ?? null;
};

const getDefaultPathname = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.pathname;
};

const deriveSurface = (pathname: string): string => {
  if (pathname.startsWith('/admin/analytics')) {
    return 'admin_analytics';
  }
  if (pathname.startsWith('/admin')) {
    return 'admin';
  }
  if (pathname.startsWith('/organization/admin')) {
    return 'organization_admin';
  }
  if (pathname.startsWith('/toolkit')) {
    return 'toolkit';
  }
  if (pathname.startsWith('/training')) {
    return 'training';
  }
  if (pathname.startsWith('/help')) {
    return 'help';
  }
  if (pathname.startsWith('/flows') || pathname.startsWith('/courses')) {
    return 'question_flow';
  }
  if (pathname.startsWith('/home')) {
    return 'home';
  }
  return 'app';
};

export const buildAnalyticsEventPayload = <T extends Record<string, unknown>>(
  eventData: T,
  user?: AnalyticsContextUser | null,
  options: BuildAnalyticsEventPayloadOptions = {},
) => {
  const pathname = options.pathname ?? getDefaultPathname();
  const currentCohort = getCurrentCohort(user);
  const screenName =
    typeof eventData.screen_name === 'string' && eventData.screen_name.trim()
      ? eventData.screen_name
      : pathname;
  const payloadWithDefaults = {
    event_version: 1,
    screen_name: screenName,
    surface:
      typeof eventData.surface === 'string' && eventData.surface.trim()
        ? eventData.surface
        : deriveSurface(screenName),
    control_id:
      typeof eventData.control_id === 'string' && eventData.control_id.trim()
        ? eventData.control_id
        : null,
    action:
      typeof eventData.action === 'string' && eventData.action.trim()
        ? eventData.action
        : null,
    user_id: eventData.user_id ?? user?.id ?? null,
    user_name: eventData.user_name ?? user?.username ?? null,
    url:
      typeof eventData.url === 'string' && eventData.url.trim()
        ? eventData.url
        : pathname,
    timestamp:
      typeof eventData.timestamp === 'string' && eventData.timestamp.trim()
        ? eventData.timestamp
        : new Date().toISOString(),
    organization_id_snapshot: user?.organization?.id ?? null,
    organization_name_snapshot: user?.organization?.name ?? null,
    cohort_id_snapshot: currentCohort?.id ?? null,
    cohort_name_snapshot: currentCohort?.name ?? null,
    cohort_role_snapshot: currentCohort?.role ?? null,
    is_staff_snapshot: Boolean(user?.is_staff),
    ...eventData,
  };

  return withDeviceContext(payloadWithDefaults);
};
